import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, RefreshCw, Send, CheckCircle, XCircle, Smartphone, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const roleLabel = (r) => (r === 'admin' ? 'Admin' : r === 'trainer' ? 'Trainer' : (r || '—'));

const AdminPushStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/push-devices/status`, authHeaders());
      setStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async (scope) => {
    setTesting(scope);
    try {
      const res = await axios.post(`${API}/admin/push-devices/test`, { scope }, authHeaders());
      const { sent, failed, fcm_available } = res.data;
      if (!fcm_available) {
        toast.error('Firebase ist nicht konfiguriert — Test kann nicht gesendet werden.');
      } else if (sent > 0) {
        toast.success(`Test-Push an ${sent} Gerät(e) gesendet${failed ? `, ${failed} fehlgeschlagen` : ''}.`);
      } else if (scope === 'mine') {
        toast.warning('Du hast kein Gerät registriert — bitte in der Admin-App einloggen.');
      } else {
        toast.warning('Kein Admin-Gerät registriert — es gibt kein Zielgerät für den Test-Push.');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setTesting('');
    }
  };

  const noDevices = status && status.registered_devices === 0;
  const coverage = status?.staff_coverage || [];
  const lastTest = status?.last_test;

  return (
    <div data-testid="admin-push-status-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900">Admin-App Push-Status</h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-push-refresh-btn"
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Diagnose, ob Admin/Trainer-Geräte für Push-Benachrichtigungen (z.&nbsp;B. bei eingehenden
        WhatsApp-Nachrichten) registriert sind und ob Firebase korrekt konfiguriert ist.
      </p>

      {loading && !status ? (
        <div className="text-sm text-gray-400">Lädt…</div>
      ) : status ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500 mb-1">Firebase</div>
              <div className="flex items-center gap-1.5 font-semibold" data-testid="admin-push-firebase-state">
                {status.firebase_configured ? (
                  <><CheckCircle className="w-4 h-4 text-green-600" /> Konfiguriert</>
                ) : (
                  <><XCircle className="w-4 h-4 text-red-600" /> Fehlt</>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500 mb-1">Registrierte Geräte</div>
              <div className="flex items-center gap-1.5 font-semibold" data-testid="admin-push-device-count">
                <Smartphone className="w-4 h-4 text-gray-500" />
                {status.registered_devices}
                <span className="text-xs font-normal text-gray-400">(deine: {status.my_devices})</span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500 mb-1">Mitarbeiter-Abdeckung</div>
              <div className="flex items-center gap-1.5 font-semibold" data-testid="admin-push-coverage-count">
                <Users className="w-4 h-4 text-gray-500" />
                {status.staff_with_device}/{status.staff_total}
                {status.staff_without_device > 0 && (
                  <span className="text-xs font-normal text-amber-600">
                    · {status.staff_without_device} ohne Gerät
                  </span>
                )}
              </div>
            </div>
          </div>

          {status.firebase_configured === false && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 mb-4" data-testid="admin-push-firebase-warning">
              ⚠️ <strong>Firebase ist nicht konfiguriert.</strong> Ohne Firebase-Service-Account können
              gar keine Pushes versendet werden. Bitte die Firebase-Konfiguration hinterlegen.
            </div>
          )}

          {noDevices && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 mb-4" data-testid="admin-push-no-devices-warning">
              ⚠️ Es ist <strong>kein Admin/Trainer-Gerät registriert</strong>. Damit können keine
              Push-Benachrichtigungen ankommen. Bitte in der Admin-App neu einloggen und sicherstellen,
              dass die <strong>aktuelle App-Version</strong> installiert ist. Danach hier „Aktualisieren".
            </div>
          )}

          {coverage.length > 0 && (
            <div className="mb-4" data-testid="admin-push-coverage-section">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Wer hat ein Gerät registriert?
              </div>
              <div className="space-y-1.5">
                {coverage.map((c) => (
                  <div
                    key={c.user_id}
                    className="flex items-center justify-between text-sm rounded-md border border-gray-100 px-3 py-1.5"
                    data-testid={`admin-push-coverage-row-${c.user_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {c.has_device ? (
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                      )}
                      <span className="truncate text-gray-800">{c.name}</span>
                      <span className="text-xs text-gray-400 shrink-0">{roleLabel(c.role)}</span>
                    </div>
                    <span className={`text-xs shrink-0 ${c.has_device ? 'text-gray-500' : 'text-red-500'}`}>
                      {c.has_device ? `${c.device_count} Gerät(e)` : 'kein Gerät'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {status.devices?.length > 0 && (
            <div className="overflow-x-auto mb-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Registrierte Geräte
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Mitarbeiter</th>
                    <th className="py-2 pr-3 font-medium">Rolle</th>
                    <th className="py-2 pr-3 font-medium">Plattform</th>
                    <th className="py-2 pr-3 font-medium">App-Version</th>
                    <th className="py-2 pr-3 font-medium">Zuletzt aktualisiert</th>
                  </tr>
                </thead>
                <tbody>
                  {status.devices.map((d, i) => (
                    <tr key={i} className="border-b border-gray-100" data-testid={`admin-push-device-row-${i}`}>
                      <td className="py-2 pr-3">{d.owner_name || <span className="text-gray-400">Unbekannt</span>}</td>
                      <td className="py-2 pr-3">{roleLabel(d.role)}</td>
                      <td className="py-2 pr-3">{d.platform || '—'}</td>
                      <td className="py-2 pr-3">{d.app_version || '—'}</td>
                      <td className="py-2 pr-3">{fmt(d.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lastTest && (
            <div className="text-xs text-gray-500 mb-3" data-testid="admin-push-last-test">
              Letzter Test ({lastTest.scope === 'mine' ? 'eigenes Gerät' : 'alle Geräte'}): {fmt(lastTest.at)} —{' '}
              {lastTest.fcm_available
                ? `${lastTest.sent} gesendet${lastTest.failed ? `, ${lastTest.failed} fehlgeschlagen` : ''}`
                : 'Firebase nicht verfügbar'}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => sendTest('mine')}
              disabled={!!testing}
              data-testid="admin-push-test-mine-btn"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <Send className={`w-4 h-4 ${testing === 'mine' ? 'animate-pulse' : ''}`} />
              Test an mein Gerät
            </button>
            <button
              onClick={() => sendTest('all')}
              disabled={!!testing}
              data-testid="admin-push-test-btn"
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <Send className={`w-4 h-4 ${testing === 'all' ? 'animate-pulse' : ''}`} />
              Test an alle Geräte
            </button>
          </div>
        </>
      ) : (
        <div className="text-sm text-gray-400">Keine Daten.</div>
      )}
    </div>
  );
};

export default AdminPushStatusCard;
